import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { graphqlQuery, graphqlMutation, graphqlSubscribe } from '../utils/graphqlApi';
import { getSessionTimeline, getAgentFeedback } from '../graphql/queries';
import { postApproval, submitAgentFeedback } from '../graphql/mutations';
import { onSessionEntry } from '../graphql/subscriptions';
import { getApprovalMessage, resolveSessionType, AWAITING_APPROVAL_STATUSES } from '../utils/sessionHelpers';
import StepExecution from './StepExecution';

const PLAN_ENTRY = 'PLAN';
const STEP_RESULT_ENTRY = 'STEP_RESULT';
const APPROVAL_RESPONSE_ENTRY = 'APPROVAL_RESPONSE';
const APPROVAL_REQUEST_ENTRY = 'APPROVAL_REQUEST';

// Maps a backend decision value to a display-friendly label/style.
const DECISION_META = {
  ALLOW: { label: 'Approved', badgeClass: 'bg-success' },
  DISCARD: { label: 'Rejected', badgeClass: 'bg-danger' },
};

// The set of DynamoDB attribute-value type tags.
const AV_KEYS = new Set(['S', 'N', 'BOOL', 'NULL', 'M', 'L', 'SS', 'NS', 'BS', 'B']);

// True if `v` is a DynamoDB attribute-value wrapper, e.g. {"S": "x"} / {"N": "1"}.
const isAttributeValue = (v) =>
  v &&
  typeof v === 'object' &&
  !Array.isArray(v) &&
  Object.keys(v).length === 1 &&
  AV_KEYS.has(Object.keys(v)[0]);

// Recursively converts DynamoDB attribute-value JSON into plain JS values.
const deepUnmarshal = (value) => {
  if (isAttributeValue(value)) {
    const [tag] = Object.keys(value);
    const inner = value[tag];
    switch (tag) {
      case 'S':
        return inner;
      case 'N':
        return Number(inner);
      case 'BOOL':
        return Boolean(inner);
      case 'NULL':
        return null;
      case 'M':
        return deepUnmarshal(inner);
      case 'L':
        return inner.map(deepUnmarshal);
      case 'NS':
        return inner.map(Number);
      case 'SS':
      case 'BS':
      case 'B':
      default:
        return inner;
    }
  }
  if (Array.isArray(value)) return value.map(deepUnmarshal);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [k, deepUnmarshal(v)])
    );
  }
  return value;
};

// Safely parse the AWSJSON `data` field and unmarshal any DynamoDB
// attribute-value wrappers. Handles double-encoded JSON.
const parseData = (raw) => {
  if (!raw) return null;
  let parsed = raw;
  for (let i = 0; i < 3 && typeof parsed === 'string'; i += 1) {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return parsed;
    }
  }
  return deepUnmarshal(parsed);
};

// Human-readable messages for a terminal outcome that was already reached
// before the user approved (info-only service needs, skipped code review, etc.).
const PRE_APPROVAL_OUTCOME_LABELS = {
  SKIPPED_CODE_REVIEW: 'Code review was skipped — this request did not require code changes.',
  COMPLETED: 'The agent completed this request successfully.',
  FAILED: 'The agent reported a failure while processing this request.',
  REJECTED: 'This request was rejected.',
  OPTIMIZATION_COMPLETED: 'Optimization completed.',
  OPTIMIZATION_NO_CHANGE: 'No changes were required.',
  NO_REPOSITORY: 'No repository was found for this service need.',
};

// Statuses that mean the agent is done.
const TERMINAL_STATUSES = new Set([
  'COMPLETED', 'FAILED', 'REJECTED', 'SKIPPED_CODE_REVIEW',
  'OPTIMIZATION_COMPLETED', 'OPTIMIZATION_NO_CHANGE', 'NO_REPOSITORY',
]);



// Finds the first STATUS/AGENT_MSG timeline entry with a terminal status.
const findTerminalOutcome = (items = []) => {
  const entry = items.find((e) => {
    if (e?.entryType !== 'STATUS' && e?.entryType !== 'AGENT_MSG') return false;
    const data = parseData(e?.data);
    const state = (data?.status || e?.status || '').toUpperCase();
    return TERMINAL_STATUSES.has(state);
  });
  if (!entry) return null;
  const data = parseData(entry?.data);
  return {
    state: (data?.status || entry?.status || '').toUpperCase(),
    detail: data?.detail || data?.message || '',
  };
};

// Severity -> badge style mapping for the structured plan header.
const SEVERITY_META = {
  LOW: 'bg-success',
  MEDIUM: 'bg-warning text-dark',
  HIGH: 'bg-danger',
  CRITICAL: 'bg-danger',
};

// Normalizes the parsed PLAN `data` into a shape the UI knows how to render.
const normalizePlan = (parsed) => {
  if (!parsed) return { kind: 'empty' };
  if (typeof parsed === 'string') return { kind: 'markdown', markdown: parsed };
  if (typeof parsed === 'object') {
    if (Array.isArray(parsed.steps)) {
      return { kind: 'structured', plan: parsed };
    }
    if (Array.isArray(parsed.suggested_questions) && parsed.suggested_questions.length > 0) {
      return { kind: 'genie-answer', data: parsed };
    }
    const md = parsed.plan || parsed.markdown || parsed.content;
    if (typeof md === 'string') return { kind: 'markdown', markdown: md };
    return { kind: 'json', json: parsed };
  }
  return { kind: 'markdown', markdown: String(parsed) };
};

// Renders a single plan step as a clean card.
function StepCard({ step, index }) {
  const dependsOn = Array.isArray(step.depends_on) ? step.depends_on : [];
  return (
    <div className="card mb-2 shadow-sm">
      <div className="card-body py-3">
        <div className="d-flex justify-content-between align-items-start gap-2 mb-1 flex-wrap">
          <h6 className="mb-0 d-flex align-items-center gap-2">
            <span className="badge bg-secondary rounded-pill">{index + 1}</span>
            <span>{step.title || step.step_id || `Step ${index + 1}`}</span>
          </h6>
          <div className="d-flex gap-1 flex-wrap">
            {step.action_type && (
              <span className="badge bg-light text-dark border">{step.action_type}</span>
            )}
            {step.requires_approval ? (
              <span className="badge bg-warning text-dark">Requires approval</span>
            ) : (
              <span className="badge bg-light text-muted border">Auto</span>
            )}
          </div>
        </div>

        {step.description && <p className="text-muted mb-2">{step.description}</p>}

        {step.command && (
          <pre
            className="bg-dark text-light p-2 rounded small mb-2"
            style={{ overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            <code>{step.command}</code>
          </pre>
        )}

        {step.expected_output && (
          <p className="mb-1 small">
            <strong>Expected output:</strong> {step.expected_output}
          </p>
        )}

        {dependsOn.length > 0 && (
          <p className="mb-0 small text-muted">
            <strong>Depends on:</strong> {dependsOn.join(', ')}
          </p>
        )}
      </div>
    </div>
  );
}

// Renders the structured plan: summary header + ordered list of step cards.
function PlanDetails({ plan }) {
  const steps = Array.isArray(plan.steps) ? plan.steps : [];
  return (
    <div>
      {plan.summary && <p className="lead mb-3">{plan.summary}</p>}

      <div className="d-flex gap-2 flex-wrap mb-3">
        {plan.severity && (
          <span className={`badge ${SEVERITY_META[plan.severity] || 'bg-secondary'}`}>
            Severity: {plan.severity}
          </span>
        )}
        {plan.estimated_duration_minutes != null && (
          <span className="badge bg-info">~{plan.estimated_duration_minutes} min</span>
        )}
        <span className="badge bg-light text-dark border">
          {steps.length} {steps.length === 1 ? 'step' : 'steps'}
        </span>
      </div>

      <h6 className="text-uppercase text-muted mb-2" style={{ letterSpacing: '0.05em' }}>
        Proposed Steps
      </h6>
      {steps.length > 0 ? (
        steps.map((step, index) => (
          <StepCard key={step.step_id || index} step={step} index={index} />
        ))
      ) : (
        <div className="alert alert-warning mb-0">This plan has no steps.</div>
      )}

      {plan.plan_id && (
        <small className="text-muted d-block mt-2">Plan ID: {plan.plan_id}</small>
      )}
    </div>
  );
}

// Condenses the normalized plan into an agent message + key data points.
const buildRejectionSummary = (planData) => {
  const summary = { agentMessage: '', dataPoints: [], steps: [], suggestedQuestions: [], json: null };
  if (!planData || planData.kind === 'empty') return summary;

  if (planData.kind === 'structured') {
    const p = planData.plan || {};
    summary.agentMessage = p.summary || 'The agent proposed an execution plan.';
    if (p.severity) summary.dataPoints.push({ label: 'Severity', value: p.severity });
    if (p.estimated_duration_minutes != null) {
      summary.dataPoints.push({ label: 'Estimated duration', value: `~${p.estimated_duration_minutes} min` });
    }
    summary.steps = Array.isArray(p.steps) ? p.steps : [];
    summary.dataPoints.push({ label: 'Proposed steps', value: String(summary.steps.length) });
    if (p.plan_id) summary.dataPoints.push({ label: 'Plan ID', value: p.plan_id });
  } else if (planData.kind === 'markdown') {
    summary.agentMessage = planData.markdown || '';
  } else if (planData.kind === 'genie-answer') {
    summary.agentMessage = planData.data?.output || 'The agent provided suggested questions.';
    summary.suggestedQuestions = Array.isArray(planData.data?.suggested_questions)
      ? planData.data.suggested_questions
      : [];
  } else if (planData.kind === 'json') {
    summary.agentMessage = 'The agent returned the following data.';
    summary.json = planData.json;
  }
  return summary;
};

// Renders a concise summary of the rejected agent message and its data.
function RejectionSummary({ planData }) {
  const summary = buildRejectionSummary(planData);
  return (
    <div>
      <div className="alert alert-danger d-flex align-items-center gap-2" role="alert">
        <span className="badge bg-danger">Rejected</span>
        <strong className="mb-0">Plan rejected — investigation closed.</strong>
      </div>

      <h6 className="text-uppercase text-muted mb-2" style={{ letterSpacing: '0.05em' }}>
        Agent Message
      </h6>
      {summary.agentMessage ? (
        <div className="investigate-report mb-3">
          <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
            {summary.agentMessage}
          </ReactMarkdown>
        </div>
      ) : (
        <p className="text-muted mb-3">No agent message was provided.</p>
      )}

      {summary.dataPoints.length > 0 && (
        <>
          <h6 className="text-uppercase text-muted mb-2" style={{ letterSpacing: '0.05em' }}>
            Summary Data
          </h6>
          <div className="d-flex gap-2 flex-wrap mb-3">
            {summary.dataPoints.map((dp) => (
              <span key={dp.label} className="badge bg-light text-dark border">
                {dp.label}: {dp.value}
              </span>
            ))}
          </div>
        </>
      )}

      {summary.steps.length > 0 && (
        <>
          <h6 className="text-uppercase text-muted mb-2" style={{ letterSpacing: '0.05em' }}>
            Proposed Steps
          </h6>
          <ol className="mb-3 ps-3">
            {summary.steps.map((step, index) => (
              <li key={step.step_id || index} className="mb-1">
                {step.title || step.step_id || `Step ${index + 1}`}
                {step.description && <span className="text-muted"> — {step.description}</span>}
              </li>
            ))}
          </ol>
        </>
      )}

      {summary.suggestedQuestions.length > 0 && (
        <>
          <h6 className="text-uppercase text-muted mb-2" style={{ letterSpacing: '0.05em' }}>
            Suggested Questions
          </h6>
          <ul className="list-group mb-0">
            {summary.suggestedQuestions.map((q, i) => (
              <li key={i} className="list-group-item">{q}</li>
            ))}
          </ul>
        </>
      )}

      {summary.json && (
        <pre
          className="bg-light p-3 rounded small mb-0"
          style={{ overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
        >
          <code>{JSON.stringify(summary.json, null, 2)}</code>
        </pre>
      )}
    </div>
  );
}

// Extracts the decision from a parsed APPROVAL_RESPONSE entry's data.
const extractApprovalData = (entry) => {
  const parsed = parseData(entry?.data);
  return {
    decision: (parsed && typeof parsed === 'object' ? parsed.decision : null) || null,
  };
};

// Picks the most recent entry by createdAt (ISO8601).
const mostRecent = (items = []) =>
  items
    .filter(Boolean)
    .slice()
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0] || null;

// Picks the earliest entry by createdAt (ISO8601).
const earliest = (items = []) =>
  items
    .filter(Boolean)
    .slice()
    .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0))[0] || null;

// Generic pending-approval detector. A "gate" is an APPROVAL_REQUEST entry (the
// primary signal the agent uses to ask for Accept/Reject) or a STATUS whose
// state is one of AWAITING_APPROVAL_STATUSES. The gate is pending (awaiting the
// user) when the most recent gate is newer than the most recent APPROVAL_RESPONSE.
// This works for the first gate AND any number of later gates (nested/repeated),
// regardless of session type.
const findPendingApproval = (items = []) => {
  const gateSignals = (items || []).filter((e) => {
    if (e?.entryType === APPROVAL_REQUEST_ENTRY) return true;
    if (e?.entryType === 'STATUS') {
      const s = (parseData(e?.data)?.status || e?.status || '').toUpperCase();
      return AWAITING_APPROVAL_STATUSES.has(s);
    }
    return false;
  });
  const latestGate = mostRecent(gateSignals);
  if (!latestGate) return null;

  const latestResponse = mostRecent((items || []).filter((e) => e?.entryType === APPROVAL_RESPONSE_ENTRY));
  // Already answered if a response came at/after the gate opened.
  if (latestResponse && new Date(latestResponse.createdAt || 0) >= new Date(latestGate.createdAt || 0)) {
    return null;
  }
  const d = parseData(latestGate.data);
  return {
    message: d?.description || d?.detail || d?.message || 'The agent is awaiting your Accept/Reject.',
  };
};

const formatTimestamp = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

function PlanApproval() {
  const { sessionId } = useParams();
  const navigate = useNavigate();

  // Resolve session type from registry — no hardcoded prefix checks in components.
  const sessionType = useMemo(() => resolveSessionType(sessionId), [sessionId]);
  const isServiceNeedSession = sessionType.label === 'Service Need';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [planEntry, setPlanEntry] = useState(null);
  const [approvalEntry, setApprovalEntry] = useState(null);

  const [decisionInput, setDecisionInput] = useState(''); // user types "allow" or "reject"
  const [submitting, setSubmitting] = useState(null); // null | 'ALLOW' | 'DISCARD'
  const [decisionMade, setDecisionMade] = useState(false);
  const [approveBanner, setApproveBanner] = useState(false);
  const [rejected, setRejected] = useState(false);
  const [actionError, setActionError] = useState('');

  // --- Agent Feedback state ---
  const [feedbackValue, setFeedbackValue] = useState(null);
  const [writtenFeedback, setWrittenFeedback] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [feedbackError, setFeedbackError] = useState('');

  // --- Stable step execution state: once execution starts, keep it visible ---
  const [executionStarted, setExecutionStarted] = useState(false);
  const stepsRef = useRef(null);

  // --- Post-approval progress (SN sessions): timeline entries after ALLOW ---
  const [postApprovalEntries, setPostApprovalEntries] = useState([]);
  const [postApprovalDone, setPostApprovalDone] = useState(false);

  // --- Approval gate (generic): typed Allow/Reject control shown whenever the
  // agent has an unanswered APPROVAL_REQUEST. Re-arms for every gate (N gates). ---
  const [reviewDecisionInput, setReviewDecisionInput] = useState(''); // user types "allow"/"reject"
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewActionError, setReviewActionError] = useState('');
  const [pendingApproval, setPendingApproval] = useState(null); // { message } while a gate awaits the user

  // Terminal outcome that was already reached BEFORE approval.
  const [preApprovalOutcome, setPreApprovalOutcome] = useState(null);
  const postApprovalPollRef = useRef(null);

  const subscriptionRef = useRef(null);

  // Once approval is ALLOW, mark execution as permanently started.
  useEffect(() => {
    if (
      !executionStarted &&
      decisionMade &&
      approvalEntry &&
      extractApprovalData(approvalEntry).decision === 'ALLOW' &&
      (isServiceNeedSession || stepsRef.current)
    ) {
      setExecutionStarted(true);
    }
  }, [executionStarted, decisionMade, approvalEntry, isServiceNeedSession]);

  // --- Post-approval polling: after ALLOW on SN sessions, poll for agent progress ---
  useEffect(() => {
    if (!isServiceNeedSession || !executionStarted || postApprovalDone) return;

    const pollStart = Date.now();
    const MAX_POLL_MS = 15 * 60 * 1000;
    const approvalTime = new Date(approvalEntry?.createdAt || 0).getTime();

    const stopPolling = () => {
      setPostApprovalDone(true);
      if (postApprovalPollRef.current) {
        clearInterval(postApprovalPollRef.current);
        postApprovalPollRef.current = null;
      }
    };

    const pollTimeline = async () => {
      if (Date.now() - pollStart > MAX_POLL_MS) {
        stopPolling();
        return;
      }
      try {
        const res = await graphqlQuery(getSessionTimeline, { sessionId });
        const rawItems = res?.getSessionTimeline?.items || [];
        // Generic gate detection (keyed on APPROVAL_REQUEST vs APPROVAL_RESPONSE):
        // re-arms the Allow/Reject control for every gate the agent opens.
        setPendingApproval(findPendingApproval(rawItems));
        const allItems = rawItems
          .filter((e) => e?.entryType !== 'APPROVAL_RESPONSE' && e?.entryType !== 'APPROVAL_REQUEST')
          .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));

        const afterApproval = allItems.filter(
          (e) => new Date(e?.createdAt || 0).getTime() > approvalTime
        );
        if (afterApproval.length > 0) {
          setPreApprovalOutcome(null);
          setPostApprovalEntries(afterApproval);
        }
        const terminalEntry = allItems.find((e) => {
          if (e?.entryType !== 'STATUS' && e?.entryType !== 'AGENT_MSG') return false;
          const data = parseData(e?.data);
          const state = (data?.status || e?.status || '').toUpperCase();
          return TERMINAL_STATUSES.has(state);
        });

        if (terminalEntry) {
          if (afterApproval.length === 0) {
            const tData = parseData(terminalEntry?.data);
            setPreApprovalOutcome({
              state: (tData?.status || terminalEntry?.status || '').toUpperCase(),
              detail: tData?.detail || tData?.message || '',
            });
          }
          stopPolling();
          return;
        }
      } catch (err) {
        console.warn('[PlanApproval] Post-approval poll error:', err.message);
      }

      if (Date.now() - pollStart > MAX_POLL_MS) {
        console.warn('[PlanApproval] Post-approval poll timed out; stopping.');
        setPostApprovalDone(true);
        if (postApprovalPollRef.current) {
          clearInterval(postApprovalPollRef.current);
          postApprovalPollRef.current = null;
        }
      }
    };

    pollTimeline();
    postApprovalPollRef.current = setInterval(pollTimeline, 5000);

    return () => {
      if (postApprovalPollRef.current) {
        clearInterval(postApprovalPollRef.current);
        postApprovalPollRef.current = null;
      }
    };
  }, [isServiceNeedSession, executionStarted, postApprovalDone, sessionId, approvalEntry]);

  // --- Fetch the latest PLAN entry and any existing APPROVAL_RESPONSE ---
  const loadTimeline = useCallback(async () => {
    if (!sessionId) {
      setError('No session id provided.');
      setLoading(false);
      return;
    }
    try {
      setError('');
      console.log('[PlanApproval] Fetching plan timeline for session:', sessionId);
      const [planRes, stepResultRes, approvalRes, fullRes] = await Promise.all([
        graphqlQuery(getSessionTimeline, { sessionId, entryType: PLAN_ENTRY }),
        graphqlQuery(getSessionTimeline, { sessionId, entryType: STEP_RESULT_ENTRY }),
        graphqlQuery(getSessionTimeline, { sessionId, entryType: APPROVAL_RESPONSE_ENTRY }),
        graphqlQuery(getSessionTimeline, { sessionId }),
      ]);

      const planItems = (planRes?.getSessionTimeline?.items || []).filter(
        (e) => e?.entryType === PLAN_ENTRY
      );
      const stepResultItems = (stepResultRes?.getSessionTimeline?.items || []).filter(
        (e) => e?.entryType === STEP_RESULT_ENTRY
      );
      const approvalItems = (approvalRes?.getSessionTimeline?.items || []).filter(
        (e) => e?.entryType === APPROVAL_RESPONSE_ENTRY
      );

      let latestPlan = mostRecent(planItems);

      if (!latestPlan && stepResultItems.length > 0) {
        const latestStepResult = mostRecent(stepResultItems);
        const parsed = parseData(latestStepResult?.data);
        const output = (parsed && typeof parsed === 'object')
          ? (parsed.output || parsed.message || JSON.stringify(parsed, null, 2))
          : (typeof parsed === 'string' ? parsed : JSON.stringify(parsed));
        latestPlan = {
          ...latestStepResult,
          entryType: PLAN_ENTRY,
          data: typeof output === 'string' ? output : JSON.stringify(output),
        };
      }

      setPlanEntry(latestPlan);

      // Generic: reflect any currently-unanswered approval gate so the typed
      // Allow/Reject control appears (works for the first gate and any later one).
      setPendingApproval(findPendingApproval(fullRes?.getSessionTimeline?.items || []));

      const existingApproval = earliest(approvalItems);

      if (existingApproval) {
        setApprovalEntry(existingApproval);
        setDecisionMade(true);
        if (extractApprovalData(existingApproval).decision === 'DISCARD') {
          setRejected(true);
        }
      } else {
        const fullItems = fullRes?.getSessionTimeline?.items || [];
        const outcome = findTerminalOutcome(fullItems);
        if (outcome) {
          setPreApprovalOutcome(outcome);
        }
      }
    } catch (err) {
      console.error('[PlanApproval] Failed to load timeline:', err);
      setError(err.message || 'Failed to load the investigation plan.');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    loadTimeline();
  }, [loadTimeline]);

  // --- Load existing feedback if any ---
  useEffect(() => {
    if (!sessionId) return;
    (async () => {
      try {
        const res = await graphqlQuery(getAgentFeedback, {
          session_id: sessionId,
          agent_id: 'orchestrator',
        });
        const existing = res?.getAgentFeedback;
        if (existing && existing.feedback) {
          setFeedbackValue(existing.feedback);
          setWrittenFeedback(existing.written_feedback || '');
          setFeedbackSubmitted(true);
        }
      } catch {
        // No existing feedback — that's fine
      }
    })();
  }, [sessionId]);

  // --- Submit agent feedback ---
  const handleFeedbackSubmit = async () => {
    if (!feedbackValue || feedbackSubmitting) return;
    setFeedbackSubmitting(true);
    setFeedbackError('');
    try {
      await graphqlMutation(submitAgentFeedback, {
        input: {
          session_id: sessionId,
          feedback: feedbackValue,
          written_feedback: writtenFeedback || null,
        },
      });
      setFeedbackSubmitted(true);
    } catch (err) {
      console.error('[PlanApproval] submitAgentFeedback failed:', err);
      setFeedbackError(err.message || 'Failed to submit feedback.');
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  // --- Real-time updates ---
  useEffect(() => {
    if (!sessionId) return undefined;

    subscriptionRef.current = graphqlSubscribe(
      onSessionEntry,
      { sessionId },
      (data) => {
        const entry = data?.onSessionEntry;
        if (!entry) return;

        if (entry.entryType === PLAN_ENTRY) {
          setPlanEntry((prev) => {
            if (!prev) return entry;
            return new Date(entry.createdAt || 0) >= new Date(prev.createdAt || 0) ? entry : prev;
          });
        } else if (entry.entryType === APPROVAL_REQUEST_ENTRY) {
          // A new gate opened — surface the typed Allow/Reject control.
          const d = parseData(entry.data);
          setPendingApproval({
            message: d?.description || d?.detail || d?.message || 'The agent is awaiting your Accept/Reject.',
          });
        } else if (entry.entryType === APPROVAL_RESPONSE_ENTRY) {
          // A gate was answered — clear the control (the next request re-arms it).
          setPendingApproval(null);
          setApprovalEntry((prev) => {
            if (prev) return prev;
            setDecisionMade(true);
            return entry;
          });
        } else if (entry.entryType === 'STATUS' || entry.entryType === 'AGENT_MSG') {
          const state = (parseData(entry.data)?.status || entry?.status || '').toUpperCase();
          if (AWAITING_APPROVAL_STATUSES.has(state)) {
            const d = parseData(entry.data);
            setPendingApproval({
              message: d?.detail || d?.message || 'The agent is awaiting your Accept/Reject.',
            });
          } else {
            const outcome = findTerminalOutcome([entry]);
            if (outcome) {
              setPendingApproval(null);
              setApprovalEntry((prevApproval) => {
                if (!prevApproval) setPreApprovalOutcome((prev) => prev || outcome);
                return prevApproval;
              });
            }
          }
        }
      },
      (err) => {
        console.warn('[PlanApproval] Subscription unavailable:', err?.message || err);
      }
    );

    return () => {
      try {
        subscriptionRef.current?.unsubscribe();
      } catch (e) {
        /* no-op */
      }
      subscriptionRef.current = null;
    };
  }, [sessionId]);

  // --- Approve / Reject helpers ---
  const decisionFromInput = (raw) => {
    const v = (raw || '').toLowerCase();
    if (v === 'allow') return 'ALLOW';
    if (v === 'reject') return 'DISCARD';
    return null;
  };

  // Plan-gate decision (the initial Approval Decision card).
  const submitDecision = async (decision) => {
    if (decisionMade || submitting) return;
    setSubmitting(decision);
    setActionError('');

    const input = { sessionId, decision };

    try {
      const result = await graphqlMutation(postApproval, { input });
      setDecisionMade(true);
      setPendingApproval(null);

      const responseEntry = result?.postApproval;
      if (responseEntry) {
        setApprovalEntry(responseEntry);
      }

      if (decision === 'ALLOW') {
        setApproveBanner(true);
      } else {
        setRejected(true);
      }
    } catch (err) {
      console.error('[PlanApproval] postApproval failed:', err);
      setActionError(err.message || 'Failed to submit your decision. Please try again.');
      setDecisionMade(false);
    } finally {
      setSubmitting(null);
    }
  };

  const handleTypedSubmit = () => {
    const decision = decisionFromInput(decisionInput);
    if (!decision) {
      setActionError('Please type "Allow" to approve or "Reject" to reject the plan.');
      return;
    }
    submitDecision(decision);
  };

  // Answers the current pending gate (any gate after the first). Not one-shot:
  // after posting we clear the control, and the next poll/subscription re-arms it
  // if the agent opens another APPROVAL_REQUEST — so any number of gates work.
  const submitReviewDecision = async () => {
    const decision = decisionFromInput(reviewDecisionInput);
    if (!decision) {
      setReviewActionError('Please type "Allow" to accept or "Reject" to reject.');
      return;
    }
    if (reviewSubmitting) return;
    setReviewSubmitting(true);
    setReviewActionError('');
    try {
      await graphqlMutation(postApproval, { input: { sessionId, decision } });
      // Hide the control immediately; the poll/subscription re-arms it when the
      // next gate (if any) opens.
      setPendingApproval(null);
      setReviewDecisionInput('');
    } catch (err) {
      console.error('[PlanApproval] approval postApproval failed:', err);
      setReviewActionError(err.message || 'Failed to submit your decision. Please try again.');
    } finally {
      setReviewSubmitting(false);
    }
  };

  // --- Render states ---
  if (loading) {
    return (
      <div className="kone-page-container">
        <div className="text-center mt-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading investigation plan...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="kone-page-container">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error</h4>
          <p>{error}</p>
          <button className="kone-btn kone-btn-primary" onClick={loadTimeline}>
            Retry
          </button>
          <button className="kone-btn kone-btn-secondary ms-2" onClick={() => navigate('/incident_view')}>
            Back
          </button>
        </div>
      </div>
    );
  }

  const decisionMeta = approvalEntry
    ? DECISION_META[extractApprovalData(approvalEntry).decision] || { label: extractApprovalData(approvalEntry).decision || 'Decision recorded', badgeClass: 'bg-secondary' }
    : null;

  const planData = planEntry ? normalizePlan(parseData(planEntry.data)) : { kind: 'empty' };

  if (planData.kind === 'structured' && Array.isArray(planData.plan.steps) && planData.plan.steps.length > 0) {
    stepsRef.current = planData.plan.steps;
  }

  const OPTIMIZATION_OUTCOME_STATES = ['OPTIMIZATION_COMPLETED', 'OPTIMIZATION_NO_CHANGE', 'NO_REPOSITORY'];
  const hasOptimizationOutcome = postApprovalEntries.some((e) => {
    const d = parseData(e?.data);
    const s = (d?.status || e?.status || '').toUpperCase();
    return OPTIMIZATION_OUTCOME_STATES.includes(s);
  });
  const visiblePostApprovalEntries = postApprovalEntries.filter((entry) => {
    const data = parseData(entry?.data);
    const entryStatus = (data?.status || entry?.status || '').toUpperCase();
    if (entryStatus === 'APPLYING_OPTIMIZATION' && hasOptimizationOutcome) return false;
    const msg = data?.detail || data?.message || data?.output || '';
    if (typeof msg === 'string' && /verify:\s*\d+\s*\/\s*\d+\s+steps?\s+succeeded/i.test(msg)) return false;
    return true;
  });

  return (
    <div className="kone-page-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="kone-section-title">Investigation Plan Review</h2>
        <button className="kone-btn kone-btn-secondary" onClick={() => navigate('/incident_view')}>
          Back
        </button>
      </div>

      {/* Plan section */}
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h5 className="mb-0">Proposed Plan</h5>
          {planEntry && (
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {planEntry.agentId && (
                <span className="badge bg-info">Agent: {planEntry.agentId}</span>
              )}
              {planEntry.status && (
                <span className="badge bg-primary">{planEntry.status}</span>
              )}
              <small className="text-muted">Created: {formatTimestamp(planEntry.createdAt)}</small>
            </div>
          )}
        </div>
        <div className="card-body">
          {!planEntry ? (
            <div className="alert alert-warning mb-0" role="alert">
              No plan has been proposed for this session yet. If the agent is still working,
              the plan will appear here once it is ready.
            </div>
          ) : planData.kind === 'structured' ? (
            <PlanDetails plan={planData.plan} />
          ) : planData.kind === 'markdown' ? (
            <div className="investigate-report">
              <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                {planData.markdown}
              </ReactMarkdown>
            </div>
          ) : planData.kind === 'genie-answer' ? (
            <div>
              {planData.data.output && (
                <div className="mb-3">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                    {planData.data.output}
                  </ReactMarkdown>
                </div>
              )}
              {!planData.data.output && planData.data.row_count === 0 && (
                <div className="alert alert-info mb-3" role="alert">
                  Genie could not find a direct answer. The following questions may help refine the investigation.
                </div>
              )}
              <h6 className="mb-2">Suggested Questions</h6>
              <ul className="list-group mb-0">
                {planData.data.suggested_questions.map((q, i) => (
                  <li key={i} className="list-group-item">{q}</li>
                ))}
              </ul>
            </div>
          ) : planData.kind === 'json' ? (
            <pre
              className="bg-light p-3 rounded small mb-0"
              style={{ overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            >
              <code>{JSON.stringify(planData.json, null, 2)}</code>
            </pre>
          ) : (
            <div className="alert alert-warning mb-0" role="alert">
              The plan entry is empty.
            </div>
          )}
        </div>
      </div>

      {/* Agent Feedback section */}
      {planEntry && (
        <div className="card mb-4">
          <div className="card-header">
            <h5 className="mb-0">Agent Feedback</h5>
          </div>
          <div className="card-body">
            {feedbackSubmitted ? (
              <div className="alert alert-success mb-0" role="alert">
                <strong>Thank you!</strong> Your feedback has been recorded.
                {feedbackValue && (
                  <span className={`badge ms-2 ${feedbackValue === 'HELPFUL' ? 'bg-success' : 'bg-warning text-dark'}`}>
                    {feedbackValue === 'HELPFUL' ? 'Helpful' : 'Not Helpful'}
                  </span>
                )}
              </div>
            ) : (
              <>
                <p className="mb-2">Was the agent's response helpful?</p>
                <div className="d-flex gap-2 mb-3">
                  <button
                    type="button"
                    className={`btn ${feedbackValue === 'HELPFUL' ? 'btn-success' : 'btn-outline-success'}`}
                    onClick={() => setFeedbackValue('HELPFUL')}
                    disabled={feedbackSubmitting}
                  >
                    &#128077; Helpful
                  </button>
                  <button
                    type="button"
                    className={`btn ${feedbackValue === 'NOT_HELPFUL' ? 'btn-danger' : 'btn-outline-danger'}`}
                    onClick={() => setFeedbackValue('NOT_HELPFUL')}
                    disabled={feedbackSubmitting}
                  >
                    &#128078; Not Helpful
                  </button>
                </div>
                <div className="mb-3">
                  <label htmlFor="written-feedback" className="form-label">
                    Additional comments (optional)
                  </label>
                  <textarea
                    id="written-feedback"
                    className="form-control"
                    rows="2"
                    placeholder="Share any additional feedback..."
                    value={writtenFeedback}
                    onChange={(e) => setWrittenFeedback(e.target.value)}
                    disabled={feedbackSubmitting}
                  />
                </div>
                <button
                  type="button"
                  className="kone-btn kone-btn-primary"
                  onClick={handleFeedbackSubmit}
                  disabled={!feedbackValue || feedbackSubmitting}
                >
                  {feedbackSubmitting ? 'Submitting...' : 'Submit Feedback'}
                </button>
                {feedbackError && (
                  <div className="alert alert-danger mt-3 mb-0" role="alert">
                    {feedbackError}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Approval section — plan-gate decision */}
      {
        <div className="card mb-4">
          <div className="card-header">
            <h5 className="mb-0">Approval Decision</h5>
          </div>
          <div className="card-body">
            {approveBanner && (
              <div className="alert alert-success" role="alert">
                <strong>Plan approved.</strong>
              </div>
            )}

            {rejected ? (
              <RejectionSummary planData={planData} />
            ) : approvalEntry ? (
              <div>
                <p className="mb-2">
                  A decision has already been recorded for this session:
                </p>
                <p className="mb-2">
                  <span className={`badge ${decisionMeta.badgeClass}`}>{decisionMeta.label}</span>
                  <span className="text-muted ms-2">{formatTimestamp(approvalEntry.createdAt)}</span>
                </p>
              </div>
            ) : preApprovalOutcome ? (
              <div
                className={`alert ${
                  preApprovalOutcome.state === 'FAILED' || preApprovalOutcome.state === 'REJECTED'
                    ? 'alert-danger'
                    : 'alert-info'
                } mb-0`}
                role="alert"
              >
                <strong>
                  {PRE_APPROVAL_OUTCOME_LABELS[preApprovalOutcome.state] || 'The agent completed this request.'}
                </strong>
                {preApprovalOutcome.detail && (
                  <div className="mt-1">{preApprovalOutcome.detail}</div>
                )}
              </div>
            ) : (
              <>
                <div className="mb-3">
                  <label htmlFor="decision-input" className="kone-label">
                    Type <strong>Allow</strong> to approve or <strong>Reject</strong> to reject this plan
                  </label>
                  <input
                    id="decision-input"
                    type="text"
                    className="kone-input"
                    autoComplete="off"
                    placeholder='Type "Allow" or "Reject"'
                    value={decisionInput}
                    onChange={(e) => {
                      setDecisionInput(e.target.value);
                      if (actionError) setActionError('');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleTypedSubmit();
                      }
                    }}
                    disabled={decisionMade || submitting !== null || !planEntry}
                  />
                </div>

                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    className="kone-btn kone-btn-primary"
                    onClick={handleTypedSubmit}
                    disabled={
                      decisionMade ||
                      submitting !== null ||
                      !planEntry ||
                      decisionFromInput(decisionInput) === null
                    }
                  >
                    {submitting === 'ALLOW'
                      ? 'Approving...'
                      : submitting === 'DISCARD'
                      ? 'Rejecting...'
                      : 'Submit Decision'}
                  </button>
                </div>

                {!planEntry && (
                  <small className="text-muted d-block mt-2">
                    Approval is disabled until a plan is available.
                  </small>
                )}
                {actionError && (
                  <div className="alert alert-danger mt-3 mb-0" role="alert">
                    {actionError}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      }

      {/* Generic mid-execution approval gate (works for all session types) */}
      {executionStarted && pendingApproval && (
        <div className="card border-warning mb-4">
          <div className="card-header">
            <h5 className="mb-0">Approval Required</h5>
          </div>
          <div className="card-body">
            <div className="alert alert-warning mb-2" role="alert">
              <strong>Approval required.</strong> {pendingApproval.message}
            </div>
            <label htmlFor="review-decision-input" className="kone-label">
              Type <strong>Allow</strong> to accept or <strong>Reject</strong> to reject
            </label>
            <input
              id="review-decision-input"
              type="text"
              className="kone-input"
              autoComplete="off"
              placeholder='Type "Allow" or "Reject"'
              value={reviewDecisionInput}
              onChange={(e) => {
                setReviewDecisionInput(e.target.value);
                if (reviewActionError) setReviewActionError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submitReviewDecision();
                }
              }}
              disabled={reviewSubmitting}
            />
            <div className="d-flex align-items-center gap-2 flex-wrap mt-2">
              <button
                type="button"
                className="kone-btn kone-btn-primary"
                onClick={submitReviewDecision}
                disabled={reviewSubmitting || decisionFromInput(reviewDecisionInput) === null}
              >
                {reviewSubmitting ? 'Submitting...' : 'Submit Decision'}
              </button>
            </div>
            {reviewActionError && (
              <div className="alert alert-danger mt-2 mb-0" role="alert">{reviewActionError}</div>
            )}
          </div>
        </div>
      )}

      {/* Post-approval flow */}
      {executionStarted && (
        isServiceNeedSession ? (
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">Agent Progress</h5>
            </div>
            <div className="card-body">
              <div className="alert alert-success mb-3" role="alert">
                <strong>{getApprovalMessage('ALLOW', sessionId)}</strong>
              </div>

              {postApprovalEntries.length === 0 && !postApprovalDone && !pendingApproval && (
                <div className="d-flex align-items-center gap-2 text-muted">
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <span>Waiting for agent to proceed...</span>
                </div>
              )}

              {postApprovalEntries.length === 0 && postApprovalDone && preApprovalOutcome && (
                <div
                  className={`alert ${
                    preApprovalOutcome.state === 'FAILED' || preApprovalOutcome.state === 'REJECTED'
                      ? 'alert-danger'
                      : 'alert-info'
                  } mb-0`}
                  role="alert"
                >
                  <strong>
                    {PRE_APPROVAL_OUTCOME_LABELS[preApprovalOutcome.state] || 'The agent completed this request.'}
                  </strong>
                  {preApprovalOutcome.detail && (
                    <div className="mt-1">{preApprovalOutcome.detail}</div>
                  )}
                </div>
              )}

              {visiblePostApprovalEntries.length > 0 && (
                <div className="timeline-entries">
                  {visiblePostApprovalEntries.map((entry, idx) => {
                    const data = parseData(entry.data);
                    const entryStatus = (data?.status || entry.status || '').toUpperCase();
                    const isTerminal = TERMINAL_STATUSES.has(entryStatus);
                    const isError = ['FAILED', 'REJECTED'].includes(entryStatus);
                    const isJira = entryStatus === 'JIRA_CREATED' || data?.issue_url;

                    const mergeRequestUrl =
                      data?.merge_request_url ||
                      data?.mergeRequestUrl ||
                      data?.mr_url ||
                      data?.pull_request_url ||
                      data?.pr_url ||
                      (typeof data?.merge_request === 'string' ? data.merge_request : data?.merge_request?.url) ||
                      null;
                    let message = '';
                    if (isJira) {
                      message = data?.message || `JIRA story created: ${data?.issue_key || ''}`;
                    } else if (entry.entryType === 'STATUS') {
                      message = data?.detail || data?.message || entryStatus;
                    } else if (entry.entryType === 'STEP_RESULT') {
                      message = data?.output || data?.message || `Step result: ${data?.status || entry.status || ''}`;
                    } else if (entry.entryType === 'AGENT_MSG') {
                      message = data?.message || data?.detail || JSON.stringify(data);
                    } else {
                      message = data?.detail || data?.message || entry.entryType;
                    }
                    const STATUS_LABELS = {
                      APPLYING_OPTIMIZATION: 'Applying Optimization',
                      OPTIMIZATION_COMPLETED: 'Optimization Completed',
                      OPTIMIZATION_NO_CHANGE: 'No Change Applied',
                      COMPLETED: 'Completed',
                      FAILED: 'Failed',
                      REJECTED: 'Rejected',
                      REVIEWING_CODE: 'Reviewing Code',
                      NO_REPOSITORY: 'No Repository Found',
                      JIRA_CREATED: 'JIRA Story Created',
                      SKIPPED_CODE_REVIEW: 'Code Review Skipped',
                    };

                    const badgeClass = isError ? 'bg-danger'
                      : isJira ? 'bg-primary'
                      : isTerminal ? 'bg-success'
                      : 'bg-info';
                    const label = STATUS_LABELS[entryStatus] || entryStatus || entry.entryType;

                    return (
                      <div key={entry.sortKey || idx} className={`mb-3 p-3 border rounded ${isError ? 'border-danger bg-danger bg-opacity-10' : isJira ? 'border-primary bg-primary bg-opacity-10' : isTerminal ? 'border-success bg-success bg-opacity-10' : ''}`}>
                        <div className="d-flex justify-content-between align-items-start mb-1">
                          <span className={`badge ${badgeClass}`}>{label}</span>
                          <small className="text-muted">{formatTimestamp(entry.createdAt)}</small>
                        </div>
                        {isJira && data?.issue_url && (
                          <div className="mt-2 mb-2">
                            <a
                              href={data.issue_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-outline-primary"
                            >
                              &#128279; {data.issue_key || 'View JIRA Story'}
                            </a>
                            {data.project && (
                              <span className="text-muted ms-2 small">Project: {data.project}</span>
                            )}
                            {data.service_need && (
                              <span className="text-muted ms-2 small">SN: {data.service_need}</span>
                            )}
                          </div>
                        )}
                        {mergeRequestUrl && (
                          <div className="mt-2 mb-2">
                            <a
                              href={mergeRequestUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-outline-success"
                            >
                              &#128279; {data.merge_request_title || data.mr_title || 'View Merge Request'}
                            </a>
                            {data.repository && (
                              <span className="text-muted ms-2 small">Repo: {data.repository}</span>
                            )}
                          </div>
                        )}
                        <div className="mt-1" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {typeof message === 'string' && message.length > 500 ? (
                            <details>
                              <summary className="text-muted small">Show details</summary>
                              <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                                {message}
                              </ReactMarkdown>
                            </details>
                          ) : (
                            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                              {typeof message === 'string' ? message : JSON.stringify(message, null, 2)}
                            </ReactMarkdown>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {!postApprovalDone && postApprovalEntries.length > 0 && !pendingApproval && (
                <div className="d-flex align-items-center gap-2 text-muted mt-2">
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Processing...</span>
                  </div>
                  <span>Agent is still processing...</span>
                </div>
              )}
            </div>
          </div>
        ) : (
          stepsRef.current && (
            <StepExecution sessionId={sessionId} steps={stepsRef.current} />
          )
        )
      )}
    </div>
  );
}

export default PlanApproval;
